import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload, X, Loader2, FileText, FileAudio, FileVideo, File as FileIcon } from "lucide-react";

/**
 * Compact inline file uploader for module resources (PDF, audio, etc).
 *
 * Props:
 *  - value: current URL (relative "/api/uploads/..." or absolute)
 *  - onChange(url, originalFilename): called after upload OR clear
 *  - accept: input accept attribute (default: docs+audio)
 *  - testId
 */
const ACCEPT_DEFAULT =
    ".pdf,.epub,.doc,.docx,.txt,.mp3,.wav,.m4a,.ogg,.aac,.mp4,.webm,application/pdf,audio/*";

function extOf(url) {
    return (url || "").split("?")[0].split(".").pop().toLowerCase();
}

function IconFor({ url }) {
    const ext = extOf(url);
    if (["mp3", "wav", "m4a", "ogg", "aac"].includes(ext)) return <FileAudio className="w-4 h-4" />;
    if (["mp4", "webm"].includes(ext)) return <FileVideo className="w-4 h-4" />;
    if (["pdf", "epub", "doc", "docx", "txt"].includes(ext)) return <FileText className="w-4 h-4" />;
    return <FileIcon className="w-4 h-4" />;
}

function shortName(url) {
    if (!url) return "";
    const last = url.split("/").pop();
    // Strip the uuid prefix we apply on the server: "abc1234567-original.pdf"
    return last.replace(/^[a-z0-9]{10}-/, "");
}

export default function FileUploader({ value, onChange, accept = ACCEPT_DEFAULT, testId = "file-uploader" }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const doUpload = async (file) => {
        setError("");
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) {
            setError("Arquivo muito grande. Máximo: 50 MB.");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/admin/uploads/file", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            onChange(data.url, data.original_name);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const clear = () => onChange("", "");

    return (
        <div data-testid={testId} className="space-y-1">
            {value ? (
                <div className="flex items-center gap-2 bg-white border border-black/10 rounded-lg px-3 py-2">
                    <IconFor url={value} />
                    <span className="font-body text-xs text-[#1A1A1A] truncate flex-1" title={value}>
                        {shortName(value)}
                    </span>
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        data-testid={`${testId}-replace`}
                        className="text-xs font-display text-[#5C5C5C] hover:text-[#1A1A1A] px-2"
                    >
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Trocar"}
                    </button>
                    <button
                        type="button"
                        onClick={clear}
                        data-testid={`${testId}-clear`}
                        className="text-red-600 hover:text-red-700"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`${testId}-pick`}
                    className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg border border-dashed border-black/15 hover:border-black/30 hover:bg-white bg-[#FAFAF7] text-sm font-display text-[#5C5C5C] transition-colors"
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Enviando..." : "Enviar arquivo"}
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                data-testid={`${testId}-input`}
                onChange={(e) => doUpload(e.target.files?.[0])}
            />

            {error && (
                <div data-testid={`${testId}-error`} className="text-xs text-red-600 bg-red-50 border border-red-100 px-2 py-1 rounded">
                    {error}
                </div>
            )}
        </div>
    );
}
